import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextInput,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 8px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }

    td {
        padding: 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
        vertical-align: middle;
    }

    tbody tr.clickable {
        cursor: pointer;
        &:hover {
            background: ${palette.graphite.cloud};
        }
    }

    .actions {
        white-space: nowrap;
        text-align: right;
    }
`;

const ProviderChoice = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
`;

const ProviderOption = styled.button`
    padding: 10px 14px;
    font-size: 13px;
    text-align: left;
    border-radius: 6px;
    cursor: pointer;
    border: 1px solid
        ${(p) =>
            p.$active
                ? palette.evergreen.aspargus
                : palette.graphite.silver};
    background: ${(p) =>
        p.$active
            ? 'rgba(46, 125, 50, 0.06)'
            : palette.graphite.white};
    color: ${palette.graphite.heavyMetal};

    strong {
        display: block;
        font-size: 13px;
        margin-bottom: 2px;
    }

    span {
        display: block;
        font-size: 12px;
        color: ${palette.graphite.charcoal};
        opacity: 0.85;
    }
`;

const emptyForm = {
    provider: 'smtp',
    name: '',
    fromAddress: '',
    host: '',
    username: '',
    password: '',
    ignoreCertErrors: true,
};

function AwarePhishSendingProfiles() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const { sendingProfiles, loadingProfiles, savingAsset } = awarePhishStore;

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        if (companyId) awarePhishStore.loadSendingProfiles(companyId);
    }, [companyId]);

    const startCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowForm(true);
    };

    const startEdit = (p) => {
        setEditingId(p.id);
        setForm({
            provider:
                p.provider === 'internal_mailgun'
                    ? 'internal_mailgun'
                    : 'smtp',
            name: p.name || '',
            fromAddress: p.fromAddress || '',
            host: p.host || '',
            username: p.username || '',
            password: '',
            ignoreCertErrors: p.ignoreCertErrors !== false,
        });
        setShowForm(true);
    };

    const save = async () => {
        try {
            const payload = { ...form };
            if (editingId && !payload.password) {
                delete payload.password;
            }
            // Editing always updates SMTP fields; provider only matters on create.
            if (editingId) {
                delete payload.provider;
            }
            await awarePhishStore.saveSendingProfile(
                companyId,
                editingId,
                payload
            );
            commonStore.success(
                editingId
                    ? 'Sending profile updated'
                    : 'Sending profile created'
            );
            setShowForm(false);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save sending profile'
            );
        }
    };

    const remove = async (p, e) => {
        if (e) e.stopPropagation();
        if (!window.confirm(`Delete sending profile “${p.name}”?`)) return;
        try {
            await awarePhishStore.removeSendingProfile(companyId, p.id);
            commonStore.success('Sending profile deleted');
        } catch (err) {
            commonStore.error(
                awarePhishStore.error || 'Failed to delete sending profile'
            );
        }
    };

    const isInternal = form.provider === 'internal_mailgun' && !editingId;

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH sending profiles</title>
            </Helmet>
            <HeaderSection
                heading="Sending profiles"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Send via AwareGO Internal Mailgun, or your own SMTP. Prefer a dedicated phishing domain — not marketing mail.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New profile"
                        mode="primary"
                        onClick={startCreate}
                        $noWrap
                    />
                }
            />
            <AwarePhishSubNav active="sending-profiles" />

            {showForm ? (
                <Block padding="20" column style={{ marginBottom: 14 }}>
                    <Heading3>
                        {editingId ? 'Edit profile' : 'New profile'}
                    </Heading3>

                    {!editingId ? (
                        <>
                            <FieldLabel>Sender type</FieldLabel>
                            <ProviderChoice>
                                <ProviderOption
                                    type="button"
                                    $active={form.provider === 'internal_mailgun'}
                                    onClick={() =>
                                        setForm({
                                            ...form,
                                            provider: 'internal_mailgun',
                                            name:
                                                form.name ||
                                                'Internal Mailgun',
                                            host: '',
                                            username: '',
                                            password: '',
                                        })
                                    }
                                >
                                    <strong>Internal Mailgun</strong>
                                    <span>
                                        Use AwareGO platform Mailgun (no SMTP
                                        credentials to enter)
                                    </span>
                                </ProviderOption>
                                <ProviderOption
                                    type="button"
                                    $active={form.provider === 'smtp'}
                                    onClick={() =>
                                        setForm({
                                            ...form,
                                            provider: 'smtp',
                                            name:
                                                form.name === 'Internal Mailgun'
                                                    ? ''
                                                    : form.name,
                                        })
                                    }
                                >
                                    <strong>Custom SMTP</strong>
                                    <span>
                                        Your own host, username and password
                                        (e.g. smtp.mailgun.org:587)
                                    </span>
                                </ProviderOption>
                            </ProviderChoice>
                        </>
                    ) : null}

                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.name}
                        onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                        }
                        placeholder={
                            isInternal
                                ? 'e.g. Internal Mailgun'
                                : 'e.g. Mailgun phishing'
                        }
                    />
                    <FieldLabel>From address</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.fromAddress}
                        onChange={(e) =>
                            setForm({ ...form, fromAddress: e.target.value })
                        }
                        placeholder="security@phish.example.com"
                    />

                    {isInternal ? (
                        <TextTiny lighter style={{ marginTop: 10 }}>
                            SMTP host and credentials are taken from platform
                            AwarePHISH / Mailgun settings. You only need a From
                            address on the allowed domain.
                        </TextTiny>
                    ) : (
                        <>
                            <FieldLabel>SMTP host</FieldLabel>
                            <TextInput
                                fullWidth
                                value={form.host}
                                onChange={(e) =>
                                    setForm({ ...form, host: e.target.value })
                                }
                                placeholder="smtp.mailgun.org:587"
                            />
                            <FieldLabel>Username</FieldLabel>
                            <TextInput
                                fullWidth
                                value={form.username}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        username: e.target.value,
                                    })
                                }
                            />
                            <FieldLabel>
                                Password
                                {editingId
                                    ? ' (leave blank to keep current)'
                                    : ''}
                            </FieldLabel>
                            <TextInput
                                fullWidth
                                type="password"
                                value={form.password}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        password: e.target.value,
                                    })
                                }
                            />
                            <label
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'center',
                                    marginTop: 12,
                                    fontSize: 13,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={form.ignoreCertErrors}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            ignoreCertErrors: e.target.checked,
                                        })
                                    }
                                />
                                Ignore certificate errors
                            </label>
                        </>
                    )}

                    <FlexDiv gap={12} style={{ marginTop: 16 }}>
                        <ThemedButton
                            text={savingAsset ? 'Saving…' : 'Save'}
                            mode="primary"
                            onClick={save}
                            disabled={savingAsset}
                            $noWrap
                        />
                        <ThemedButton
                            text="Cancel"
                            mode="secondary"
                            onClick={() => setShowForm(false)}
                            $noWrap
                        />
                    </FlexDiv>
                </Block>
            ) : null}

            <Block tablePadding column>
                {loadingProfiles && !(sendingProfiles || []).length ? (
                    <TextDefault lighter>Loading…</TextDefault>
                ) : !(sendingProfiles || []).length ? (
                    <TextDefault lighter>
                        No sending profiles yet — create one before launching a
                        simulation.
                    </TextDefault>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>From</th>
                                <th>Type / host</th>
                                <th className="actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(sendingProfiles || []).map((p) => (
                                <tr
                                    key={p.id}
                                    className="clickable"
                                    onClick={() => startEdit(p)}
                                >
                                    <td>
                                        <TextDefault bold>{p.name}</TextDefault>
                                    </td>
                                    <td>
                                        <TextDefault>
                                            {p.fromAddress}
                                        </TextDefault>
                                    </td>
                                    <td>
                                        <TextTiny lighter>
                                            {p.provider === 'internal_mailgun'
                                                ? 'Internal Mailgun'
                                                : p.host || 'SMTP'}
                                        </TextTiny>
                                    </td>
                                    <td className="actions">
                                        <FlexDiv
                                            gap={8}
                                            style={{
                                                justifyContent: 'flex-end',
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            <ThemedButton
                                                text="Edit"
                                                mode="secondary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    startEdit(p);
                                                }}
                                                $noWrap
                                            />
                                            <ThemedButton
                                                text="Delete"
                                                mode="secondary"
                                                onClick={(e) => remove(p, e)}
                                                $noWrap
                                            />
                                        </FlexDiv>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwarePhishSendingProfiles);
